import { Context, Layer, Redacted } from "effect";

export const OTEL_EXPORTER_OTLP_PROTOCOL_VALUES = [
  "grpc",
  "http/protobuf",
] as const;

export type OTelExporterOtlpProtocol =
  (typeof OTEL_EXPORTER_OTLP_PROTOCOL_VALUES)[number];

export type ServerConfigShape = {
  readonly baseUrl: string;
  readonly version: string;
  readonly database: {
    readonly url: Redacted.Redacted;
  };
  readonly environment: "dev" | "prod" | "test";
  readonly sentry: {
    readonly dsn: string | undefined;
  };
  readonly otel: {
    readonly tracesEnabled: boolean;
    readonly exporterOtlpEndpoint: string | undefined;
    readonly exporterOtlpHeaders:
      | Redacted.Redacted
      | undefined;
    readonly exporterOtlpProtocol: OTelExporterOtlpProtocol;
    readonly exporterOtlpTimeoutMs: number;
  };
  readonly auth: {
    readonly secret: Redacted.Redacted;
    readonly url: string;
  };
  readonly email: {
    readonly brevoApiKey: Redacted.Redacted;
    readonly contactFormRecipients: string;
  };
  readonly github: {
    readonly appId: string;
    readonly appName: string;
    readonly appClientId: string;
    readonly appClientSecret: Redacted.Redacted;
    readonly appPrivateKey: Redacted.Redacted;
    readonly appWebhookSecret: Redacted.Redacted;
  };
  readonly google: {
    readonly clientId: string;
    readonly clientSecret: Redacted.Redacted;
  };
};

export class ServerConfig extends Context.Service<
  ServerConfig,
  ServerConfigShape
>()("@vivus/ServerConfig") {}

export type ServerConfigEnv = {
  BASE_URL: string;
  BETTER_AUTH_SECRET: string;
  BETTER_AUTH_URL: string;
  BREVO_API_KEY: string;
  CONTACT_FORM_RECIPIENTS: string;
  DATABASE_URL: string;
  ENVIRONMENT: string;
  GH_APP_CLIENT_ID: string;
  GH_APP_CLIENT_SECRET: string;
  GH_APP_ID: string;
  GH_APP_NAME: string;
  GH_APP_PRIVATE_KEY_BASE64: string;
  GH_APP_WEBHOOK_SECRET: string;
  GOOGLE_CLIENT_ID: string;
  GOOGLE_CLIENT_SECRET: string;
  OTEL_EXPORTER_OTLP_ENDPOINT?: string | undefined;
  OTEL_EXPORTER_OTLP_HEADERS?: string | undefined;
  OTEL_EXPORTER_OTLP_PROTOCOL: OTelExporterOtlpProtocol;
  OTEL_EXPORTER_OTLP_TIMEOUT_MS: number;
  OTEL_TRACES_ENABLED: boolean;
  SENTRY_DSN?: string | undefined;
  VERSION: string;
};

export const makeServerConfigLayer = (
  env: ServerConfigEnv
) =>
  Layer.succeed(ServerConfig, {
    baseUrl: env.BASE_URL,
    version: env.VERSION,
    database: { url: Redacted.make(env.DATABASE_URL) },
    environment: env.ENVIRONMENT as "dev" | "prod" | "test",
    sentry: { dsn: env.SENTRY_DSN },
    otel: {
      tracesEnabled: env.OTEL_TRACES_ENABLED,
      exporterOtlpEndpoint:
        env.OTEL_EXPORTER_OTLP_ENDPOINT?.trim() ||
        undefined,
      exporterOtlpHeaders:
        env.OTEL_EXPORTER_OTLP_HEADERS?.trim()
          ? Redacted.make(env.OTEL_EXPORTER_OTLP_HEADERS)
          : undefined,
      exporterOtlpProtocol: env.OTEL_EXPORTER_OTLP_PROTOCOL,
      exporterOtlpTimeoutMs:
        env.OTEL_EXPORTER_OTLP_TIMEOUT_MS,
    },
    auth: {
      secret: Redacted.make(env.BETTER_AUTH_SECRET),
      url: env.BETTER_AUTH_URL,
    },
    email: {
      brevoApiKey: Redacted.make(env.BREVO_API_KEY),
      contactFormRecipients: env.CONTACT_FORM_RECIPIENTS,
    },
    github: {
      appId: env.GH_APP_ID,
      appName: env.GH_APP_NAME,
      appClientId: env.GH_APP_CLIENT_ID,
      appClientSecret: Redacted.make(
        env.GH_APP_CLIENT_SECRET
      ),
      appPrivateKey: Redacted.make(
        Buffer.from(
          env.GH_APP_PRIVATE_KEY_BASE64,
          "base64"
        ).toString("utf-8")
      ),
      appWebhookSecret: Redacted.make(
        env.GH_APP_WEBHOOK_SECRET
      ),
    },
    google: {
      clientId: env.GOOGLE_CLIENT_ID,
      clientSecret: Redacted.make(env.GOOGLE_CLIENT_SECRET),
    },
  });
